import { DemoResetButton } from '@/components/portal/demo-reset-button'
import { PORTAL_HR_ANCHOR, usePortalHrFtue } from '@/components/portal/ftue'
import { InternalMobilityReadinessBanner } from '@/components/portal/internal-mobility/readiness-banner'
import { OrgLogo } from '@/components/portal/org-logo'
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog'
import { type PortalRouteAccessId, canAccessPortalRoute } from '@/config/route-access'
import { FtueHost, useFtueAnchor, useFtueServerSync } from '@/ftue'
import { useAccessControl } from '@/hooks/use-access-control'
import { useAuth } from '@/hooks/use-auth'
import { useOrgBranding } from '@/hooks/use-org-branding'
import { enableBenefits, enableCostSavings, enableSeveranceEngine } from '@/lib/feature-flags'
import { cn } from '@/lib/utils'
import type { User } from '@/types'
import {
  BriefcaseBusiness,
  ContactRound,
  FileText,
  Heart,
  Home,
  Link2,
  LogOut,
  Menu,
  Users,
} from 'lucide-react'
import { useState } from 'react'
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom'

interface PortalNavChild {
  name: string
  href: string
  routeId: PortalRouteAccessId
  disabled?: boolean
}

interface PortalNavItem {
  name: string
  href: string
  icon: typeof Home
  /** Routes that keep this item highlighted, including its children's. */
  matchPaths: string[]
  routeId: PortalRouteAccessId
  children?: PortalNavChild[]
}

// People import creates/updates people from base columns only. Severance
// terms are a separate, recurring HR action (setting/updating negotiated
// terms for people who already exist) with its own entry point under
// Severance, including the "import terms" worksheet that used to live here.
function buildPortalNavItems(
  hasSeveranceEntitlement: boolean,
  hasHrisEntitlement: boolean,
): PortalNavItem[] {
  return [
    {
      name: 'Dashboard',
      href: '/dashboard',
      icon: Home,
      matchPaths: ['/dashboard'],
      routeId: 'portal.root',
    },
    // Connecting an HR system is the primary onboarding step -- People is
    // meant to populate from it, with CSV import as the manual fallback --
    // so it leads the nav rather than trailing everything else.
    ...(hasHrisEntitlement ? [hrisNavItem] : []),
    {
      name: 'People',
      href: '/people',
      icon: Users,
      matchPaths: ['/people', '/import', '/imports', '/unmatched'],
      routeId: 'portal.people',
      children: [
        { name: 'Import', href: '/import', routeId: 'portal.people.manage' },
        { name: 'Import History', href: '/imports', routeId: 'portal.people.manage' },
        // Only meaningful once an HR system is attached, but harmless otherwise --
        // with no connection there is nothing to reconcile and the page says so.
        ...(enableBenefits
          ? [
              {
                name: 'Sync issues',
                href: '/unmatched',
                routeId: 'portal.people.manage' as const,
              },
            ]
          : []),
      ],
    },
    {
      name: 'Stakeholders',
      href: '/stakeholders',
      icon: ContactRound,
      matchPaths: ['/stakeholders'],
      routeId: 'portal.stakeholders.manage',
    },
    {
      name: 'Internal Mobility',
      href: '/internal-mobility',
      icon: BriefcaseBusiness,
      matchPaths: [
        '/internal-mobility',
        '/internal-jobs',
        '/internal-mobility/department-needs',
        '/internal-mobility/candidates',
        '/internal-mobility/organization-chart',
        '/internal-mobility/matrix',
      ],
      routeId: 'portal.internal-jobs',
      children: [
        {
          name: 'Organization Chart',
          href: '/internal-mobility/organization-chart',
          routeId: 'portal.internal-jobs',
        },
        { name: 'Internal Jobs', href: '/internal-jobs', routeId: 'portal.internal-jobs' },
        {
          name: 'Candidates',
          href: '/internal-mobility/candidates',
          routeId: 'portal.internal-jobs',
        },
        {
          name: 'Internal Mobility Matrix',
          href: '/internal-mobility/matrix',
          routeId: 'portal.internal-jobs',
        },
      ],
    },
    // enableBenefits is the environment build flag; hasSeveranceEntitlement is
    // the per-org entitlement (features.severance, mirrors features.hris) --
    // both must be on, or every org sharing the environment sees this nav.
    ...(enableBenefits && hasSeveranceEntitlement
      ? [
          {
            name: 'Severance',
            href: '/benefits',
            icon: Heart,
            matchPaths: ['/benefits', '/cost-savings', '/severance-import', '/severance-employees'],
            routeId: 'portal.severance' as const,
            children: [
              { name: 'Elections', href: '/benefits', routeId: 'portal.severance' as const },
              ...(enableSeveranceEngine
                ? [
                    {
                      name: 'Severance Employees',
                      href: '/severance-employees?tab=people',
                      routeId: 'portal.severance' as const,
                    },
                    {
                      name: 'Cohorts',
                      href: '/severance-employees?tab=cohorts',
                      routeId: 'portal.severance' as const,
                    },
                  ]
                : []),
              {
                name: 'Import Terms',
                href: '/severance-import',
                routeId: 'portal.severance.manage' as const,
              },
              // Hidden until the ROI calculation is verified (PEL-910).
              ...(enableCostSavings
                ? [
                    {
                      name: 'Cost & Savings',
                      href: '/cost-savings',
                      routeId: 'portal.severance' as const,
                    },
                  ]
                : []),
            ],
          },
        ]
      : []),
    {
      name: 'Benefits',
      href: '/exit-guide-documents',
      icon: FileText,
      matchPaths: ['/exit-guide-documents'],
      routeId: 'portal.documents.manage',
    },
  ]
}

const hrisNavItem: PortalNavItem = {
  name: 'HR System',
  href: '/hris',
  icon: Link2,
  matchPaths: ['/hris'],
  routeId: 'portal.root',
}

interface PortalSidebarContentProps {
  navItems: PortalNavItem[]
  pathname: string
  search: string
  orgName: string | null
  logoUrl: string | null
  user: User | null
  onLogout: () => void
  isLoggingOut: boolean
  /** Closes the mobile drawer; undefined on the always-visible desktop sidebar. */
  onNavigate?: () => void
}

function matchesNavigationPath(pathname: string, target: string, search = '') {
  const [path = '', query] = target.split('?')
  if (pathname !== path && !pathname.startsWith(`${path}/`)) return false
  if (!query) return true
  const current = new URLSearchParams(search)
  return [...new URLSearchParams(query)].every(
    ([key, value]) => (current.get(key) ?? (key === 'tab' ? 'people' : null)) === value,
  )
}

function PortalSidebarContent({
  navItems,
  pathname,
  search,
  orgName,
  logoUrl,
  user,
  onLogout,
  isLoggingOut,
  onNavigate,
}: PortalSidebarContentProps) {
  // FTUE tour anchors for the sidebar links it walks the HR admin through. One
  // id per target; the same id is registered by both the desktop and mobile
  // copies of this component and the registry resolves to whichever is on screen.
  const hrisNavAnchor = useFtueAnchor(PORTAL_HR_ANCHOR.navHris)
  const peopleNavAnchor = useFtueAnchor(PORTAL_HR_ANCHOR.navPeople)
  const stakeholdersNavAnchor = useFtueAnchor(PORTAL_HR_ANCHOR.navStakeholders)
  const exitGuideNavAnchor = useFtueAnchor(PORTAL_HR_ANCHOR.navExitGuide)
  const internalJobsNavAnchor = useFtueAnchor(PORTAL_HR_ANCHOR.navInternalJobs)
  // Parent links: hrefs never collide across items, so href alone identifies one.
  const navAnchorFor = (href: string): ((el: HTMLElement | null) => void) | undefined => {
    switch (href) {
      case '/hris':
        return hrisNavAnchor
      case '/people':
        return peopleNavAnchor
      case '/stakeholders':
        return stakeholdersNavAnchor
      case '/exit-guide-documents':
        return exitGuideNavAnchor
      case '/internal-jobs':
        return internalJobsNavAnchor
      default:
        return undefined
    }
  }

  const importNavAnchor = useFtueAnchor(PORTAL_HR_ANCHOR.navImport)
  const severanceImportNavAnchor = useFtueAnchor(PORTAL_HR_ANCHOR.navSeveranceImport)
  const severanceElectionsNavAnchor = useFtueAnchor(PORTAL_HR_ANCHOR.navSeveranceElections)
  // Child links: keyed on `name`, not `href` -- Severance's "Elections" child
  // and its own "Severance" parent both point at /benefits, so href alone
  // can't tell them apart (a case for '/benefits' in navAnchorFor above would
  // wrongly anchor both). Every child `name` is unique within the sidebar.
  const childNavAnchorFor = (name: string): ((el: HTMLElement | null) => void) | undefined => {
    switch (name) {
      case 'Import':
        return importNavAnchor
      case 'Import Terms':
        return severanceImportNavAnchor
      case 'Elections':
        return severanceElectionsNavAnchor
      default:
        return undefined
    }
  }

  return (
    <>
      <div className="px-5 py-5 border-b flex items-center gap-2.5">
        <OrgLogo logoUrl={logoUrl} orgName={orgName} />
        {!logoUrl && (
          <span className="text-lg font-semibold tracking-tight text-foreground">
            {orgName || 'Portal'}
          </span>
        )}
      </div>
      <nav className="flex flex-col gap-0.5 px-3 py-4 flex-1">
        {navItems.map((item) => {
          const Icon = item.icon
          const isActive = item.matchPaths.some((path) => matchesNavigationPath(pathname, path))

          return (
            <div key={item.href}>
              <Link
                ref={navAnchorFor(item.href)}
                to={item.href}
                onClick={onNavigate}
                className={cn(
                  'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                  isActive
                    ? 'bg-teal-50 text-teal-800'
                    : 'text-muted-foreground hover:bg-muted/80 hover:text-foreground',
                )}
              >
                <Icon className={cn('h-4 w-4', isActive && 'text-teal-700')} />
                {item.name}
              </Link>

              {item.children && (
                <div className="mb-1 ml-6 flex flex-col gap-0.5 border-l pl-3">
                  {item.children.map((child) =>
                    child.disabled ? (
                      <span
                        key={child.name}
                        aria-disabled="true"
                        className="block cursor-not-allowed px-2 py-1 text-[13px] text-muted-foreground/50"
                      >
                        {child.name}
                      </span>
                    ) : (
                      <Link
                        key={child.name}
                        ref={childNavAnchorFor(child.name)}
                        to={child.href}
                        onClick={onNavigate}
                        className={cn(
                          'block px-2 py-1 text-[13px] rounded-md transition-colors',
                          matchesNavigationPath(pathname, child.href, search)
                            ? 'bg-teal-100 text-teal-800'
                            : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                        )}
                      >
                        {child.name}
                      </Link>
                    ),
                  )}
                </div>
              )}
            </div>
          )
        })}
      </nav>
      <div className="border-t px-3 py-3">
        {user && (
          <div className="flex items-center gap-3 px-3 py-2">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-brand-100 text-sm font-semibold text-brand-dark">
              {(user.first_name?.[0] || user.email?.[0] || 'U').toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-foreground truncate">
                {user.full_name || user.first_name || user.email}
              </p>
              {user.email && <p className="text-xs text-muted-foreground truncate">{user.email}</p>}
            </div>
            <button
              type="button"
              onClick={onLogout}
              disabled={isLoggingOut}
              className="ml-2 p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              title="Sign out"
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        )}
        {/* Self-gates on VITE_ENABLE_DEMO_RESET + the signed-in org's slug
            being "delgo" -- renders nothing for every other org/environment. */}
        <div className="px-3 pt-2">
          <DemoResetButton />
        </div>

        <div className="flex items-center gap-1.5 px-3 pt-2 text-[11px] text-muted-foreground/70">
          <span>Powered by Pelgo</span>
        </div>
      </div>
    </>
  )
}

export function PortalLayout() {
  const location = useLocation()
  const navigate = useNavigate()
  const { orgName, logoUrl, features } = useOrgBranding()
  const { logout, isLoggingOut, user } = useAuth()
  const authorization = useAccessControl()
  const [mobileNavOpen, setMobileNavOpen] = useState(false)
  const showInternalMobilityReadiness = [
    '/internal-mobility',
    '/internal-jobs',
    '/internal-mobility/organization-chart',
    '/internal-mobility/department-needs',
    '/internal-mobility/candidates',
    '/internal-mobility/matrix',
  ].some((path) => matchesNavigationPath(location.pathname, path))

  // Entitlement is per-org and server-enforced; this only hides the entry point.
  const candidateNavItems = buildPortalNavItems(!!features.severance, !!features.hris)
  const navItems = authorization.data
    ? candidateNavItems
        .filter((item) =>
          canAccessPortalRoute(authorization.data, item.routeId, user?.organization_id),
        )
        .map((item) => ({
          ...item,
          ...(item.children
            ? {
                children: item.children.filter((child) =>
                  canAccessPortalRoute(authorization.data, child.routeId, user?.organization_id),
                ),
              }
            : {}),
        }))
    : []
  const canAccess = (routeId: PortalRouteAccessId) =>
    !!authorization.data && canAccessPortalRoute(authorization.data, routeId, user?.organization_id)

  // FTUE first-run tour: hydrate/persist the engine against the signed-in user's
  // account state, and register the dashboard + sidebar-nav steps. The renderer
  // (<FtueHost/>) is mounted once below. No SSE bridge -- the portal tour has no
  // event-gated steps.
  useFtueServerSync()
  usePortalHrFtue({
    hasHris: !!features.hris && canAccess('portal.root'),
    hasImport: canAccess('portal.people.manage'),
    hasStakeholders: canAccess('portal.stakeholders.manage'),
    hasSeverance: !!features.severance && canAccess('portal.severance'),
    hasSeveranceManagement: !!features.severance && canAccess('portal.severance.manage'),
    hasInternalJobs: canAccess('portal.internal-jobs'),
  })

  const handleLogout = async () => {
    try {
      await logout()
      navigate('/login', { replace: true })
    } catch {
      // The shared session banner explains that secure logout did not complete.
    }
  }

  return (
    <div className="flex flex-col h-screen overflow-hidden bg-[#102A56]">
      {/* Slim navy header carries the Pelgo wordmark; the org's own logo stays in the sidebar below */}
      <header className="hidden md:flex h-12 shrink-0 items-center px-5">
        <img src="/logomark-white.png" alt="Pelgo" className="h-[20px] w-auto max-w-[100px]" />
      </header>

      {/* Mobile-only top bar: the sidebar below is desktop-only, so this is the sole way to reach nav under md */}
      <div className="flex md:hidden h-12 shrink-0 items-center justify-between px-4">
        <img src="/logomark-white.png" alt="Pelgo" className="h-[18px] w-auto max-w-[90px]" />
        <button
          type="button"
          onClick={() => setMobileNavOpen(true)}
          className="p-1.5 -mr-1.5 rounded-md text-white/80 hover:text-white hover:bg-white/10 transition-colors"
          aria-label="Open navigation"
        >
          <Menu className="h-5 w-5" />
        </button>
      </div>

      <Dialog open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
        <DialogContent className="left-0 top-0 h-full w-64 max-w-[85vw] translate-x-0 translate-y-0 grid-rows-[auto_1fr_auto] gap-0 rounded-none border-r p-0 data-[state=closed]:slide-out-to-left-full data-[state=open]:slide-in-from-left-full data-[state=closed]:slide-out-to-top-0 data-[state=open]:slide-in-from-top-0 sm:rounded-none">
          <DialogTitle className="sr-only">Navigation</DialogTitle>
          <PortalSidebarContent
            navItems={navItems}
            pathname={location.pathname}
            search={location.search}
            orgName={orgName}
            logoUrl={logoUrl}
            user={user}
            onLogout={handleLogout}
            isLoggingOut={isLoggingOut}
            onNavigate={() => setMobileNavOpen(false)}
          />
        </DialogContent>
      </Dialog>

      <div className="relative flex flex-1 min-h-0 md:gap-2 md:px-2 md:pb-2">
        <div className="flex flex-1 min-w-0 min-h-0 rounded-lg border border-slate-200 bg-white overflow-hidden">
          {/* Sidebar (desktop only; mobile uses the drawer above) */}
          <aside className="hidden md:flex w-64 border-r bg-background flex-col shrink-0">
            <PortalSidebarContent
              navItems={navItems}
              pathname={location.pathname}
              search={location.search}
              orgName={orgName}
              logoUrl={logoUrl}
              user={user}
              onLogout={handleLogout}
              isLoggingOut={isLoggingOut}
            />
          </aside>

          {/* Main content */}
          <main className="relative min-w-0 flex-1 overflow-hidden bg-[#F3F4F6]">
            <div className="h-full min-w-0 overflow-auto">
              <Outlet />
            </div>
            {showInternalMobilityReadiness && <InternalMobilityReadinessBanner />}
          </main>
        </div>
      </div>

      {/* FTUE coachmark renderer -- portals into document.body (PEL-516). */}
      <FtueHost />
    </div>
  )
}
